import { Fragment, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode } from 'react';

export function Card({ children, padded, className = '', as: Tag = 'div', ...rest }: {
  children: ReactNode;
  padded?: boolean;
  className?: string;
  as?: 'div' | 'section' | 'article' | 'aside';
} & Record<string, unknown>) {
  return (
    <Tag className={`hv-card${padded ? ' hv-card--pad' : ''} ${className}`.trim()} {...rest}>
      {children}
    </Tag>
  );
}

export function Hairline({ vertical }: { vertical?: boolean }) {
  return vertical ? <span aria-hidden="true" className="hv-hairline--v" /> : <hr className="hv-hairline" />;
}

export interface Crumb {
  label: ReactNode;
  href?: string;
}

/** network › country › city › venue. Pass a link component for SPA navigation. */
export function Breadcrumbs({
  items,
  renderLink,
}: {
  items: Crumb[];
  renderLink?: (href: string, label: ReactNode) => ReactNode;
}) {
  return (
    <nav aria-label="Breadcrumb" className="hv-crumbs">
      {items.map((c, i) => {
        const last = i === items.length - 1;
        return (
          <Fragment key={i}>
            {last ? (
              <span className="hv-crumbs__current" aria-current="page">
                {c.label}
              </span>
            ) : c.href ? (
              renderLink ? renderLink(c.href, c.label) : <a href={c.href}>{c.label}</a>
            ) : (
              <span>{c.label}</span>
            )}
            {!last && (
              <span aria-hidden="true" className="hv-crumbs__sep">
                ›
              </span>
            )}
          </Fragment>
        );
      })}
    </nav>
  );
}

type Variant = 'primary' | 'secondary' | 'outline-accent' | 'link' | 'icon';

export function Button({
  variant = 'secondary',
  size,
  block,
  className = '',
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: 'sm' | 'lg'; block?: boolean }) {
  const cls = ['hv-btn', `hv-btn--${variant}`, size && `hv-btn--${size}`, block && 'hv-btn--block', className].filter(Boolean).join(' ');
  return <button type="button" className={cls} {...rest} />;
}

export function buttonClass(variant: Variant = 'secondary', opts: { size?: 'sm' | 'lg'; block?: boolean } = {}): string {
  return ['hv-btn', `hv-btn--${variant}`, opts.size && `hv-btn--${opts.size}`, opts.block && 'hv-btn--block'].filter(Boolean).join(' ');
}

export function Chip({ selected, children, ...rest }: ButtonHTMLAttributes<HTMLButtonElement> & { selected?: boolean }) {
  return (
    <button type="button" className="hv-chip" aria-pressed={Boolean(selected)} {...rest}>
      {children}
    </button>
  );
}

export function Field({ label, id, ...input }: InputHTMLAttributes<HTMLInputElement> & { label: string; id: string }) {
  return (
    <div className="hv-field">
      <label className="hv-field__label" htmlFor={id}>
        {label}
      </label>
      <input id={id} className="hv-input" {...input} />
    </div>
  );
}

export function Toggle({ label, checked, onChange }: { label: string; checked: boolean; onChange(checked: boolean): void }) {
  return (
    <label className="hv-toggle">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span aria-hidden="true" className="hv-toggle__track" />
      <span>{label}</span>
    </label>
  );
}

export function ErrorNotice({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div role="alert" className="hv-alert hv-alert--error">
      <span aria-hidden="true" className="hv-alert__icon">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
          <circle cx="12" cy="12" r="9" />
          <path d="M12 8v4M12 16h.01" />
        </svg>
      </span>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <span className="hv-alert__title">{title}</span>
        {children && <span className="hv-alert__body">{children}</span>}
      </div>
    </div>
  );
}
